/**
 * Anthropic API client — single-PDF invoice extraction.
 *
 * Uses tool calling (`record_invoice`) for structured output: the model is
 * forced to emit a tool_use block matching our schema, which is far more
 * reliable than asking for JSON-in-text.
 *
 * Calls api.anthropic.com directly from the browser. This requires the
 * `anthropic-dangerous-direct-browser-access: true` header. Per spec §05,
 * this is appropriate for a single-user, BYO-key personal tool.
 */

const API_URL = 'https://api.anthropic.com/v1/messages';
const DEFAULT_MODEL = 'claude-sonnet-4-6';
const MAX_TOKENS = 4096;

const SYSTEM_PROMPT = `You are an invoice data extraction tool. The user will give you a single scanned invoice PDF. Read it carefully and call the record_invoice tool exactly once with the extracted fields.

Rules:
- If you cannot determine a field confidently, pass null for that field. Never guess.
- Do NOT include GL codes, property codes, or accounting categorizations. Those are applied by separate rules and are not your job.
- For amounts, return decimal numbers without currency symbols or thousands separators (e.g. 1234.56, not "$1,234.56").
- Dates must be in YYYY-MM-DD format.
- Currency is the ISO 4217 code (USD, EUR, etc.). Default to USD if not stated.
- For each line item, include item_code exactly as printed on the invoice, or null if no item code appears. Never invent or normalize item codes.
- sales_tax_lines: array of {label, amount} for each separate tax line, or null if not applicable. If a single total tax exists, use one element. When both tax_amount and sales_tax_lines are present, they should agree.
- raw_text is your verbatim transcription of the invoice's text content, used for review purposes.`;

const RECORD_INVOICE_TOOL = {
  name: 'record_invoice',
  description: 'Record the structured fields extracted from one invoice PDF.',
  input_schema: {
    type: 'object',
    properties: {
      vendor_name: {
        type: ['string', 'null'],
        description: "The vendor's name as printed on the invoice. Raw, unmodified."
      },
      invoice_number: {
        type: ['string', 'null'],
        description: "The vendor's invoice ID/number."
      },
      invoice_date: {
        type: ['string', 'null'],
        description: 'Invoice issue date in YYYY-MM-DD format. Null if not shown.'
      },
      due_date: {
        type: ['string', 'null'],
        description: 'Due date in YYYY-MM-DD format. Null if not shown.'
      },
      total_amount: {
        type: ['number', 'null'],
        description: 'Total amount as a decimal. No currency symbol. Null if missing.'
      },
      tax_amount: {
        type: ['number', 'null'],
        description: 'Tax amount as a separate decimal. Null if not broken out.'
      },
      currency: {
        type: 'string',
        description: 'ISO 4217 currency code. Default "USD" if not stated.'
      },
      line_items: {
        type: 'array',
        description: 'Line items on the invoice. Empty array if none discernible.',
        items: {
          type: 'object',
          properties: {
            description: { type: ['string', 'null'] },
            quantity:    { type: ['number', 'null'] },
            unit_price:  { type: ['number', 'null'] },
            line_total:  { type: ['number', 'null'] },
            item_code:   { type: ['string', 'null'], description: "Vendor's SKU or service code as printed; null if absent." }
          }
        }
      },
      sales_tax_lines: {
        type: ['array', 'null'],
        description: 'Separate tax lines with label and amount, or null if not applicable.',
        items: {
          type: 'object',
          properties: {
            label:  { type: ['string', 'null'] },
            amount: { type: ['number', 'null'] }
          }
        }
      },
      raw_text: {
        type: 'string',
        description: 'Verbatim transcription of the invoice text. Used for review purposes.'
      }
    },
    required: ['vendor_name', 'invoice_number', 'invoice_date', 'total_amount', 'currency', 'line_items', 'sales_tax_lines', 'raw_text']
  }
};

/**
 * Extract a single PDF.
 * @param {File}   file     - the PDF File object
 * @param {string} apiKey   - Anthropic API key
 * @param {Object} [options]
 * @param {string} [options.model]      - model string (default Sonnet 4.6)
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{data: object, raw: object, ms: number}>}
 */
export async function extractInvoice(file, apiKey, options = {}) {
  if (!apiKey) throw new Error('Missing API key.');

  const t0 = performance.now();
  const base64 = await fileToBase64(file);

  const body = {
    model: options.model || DEFAULT_MODEL,
    max_tokens: MAX_TOKENS,
    system: SYSTEM_PROMPT,
    tools: [RECORD_INVOICE_TOOL],
    tool_choice: { type: 'tool', name: 'record_invoice' },
    messages: [
      {
        role: 'user',
        content: [
          {
            type: 'document',
            source: { type: 'base64', media_type: 'application/pdf', data: base64 }
          },
          { type: 'text', text: 'Extract this invoice. Call the record_invoice tool exactly once.' }
        ]
      }
    ]
  };

  const res = await fetch(API_URL, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true'
    },
    body: JSON.stringify(body),
    signal: options.signal
  });

  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    let parsed;
    try { parsed = JSON.parse(errText); } catch { /* ignore */ }
    const msg = parsed?.error?.message || errText || `HTTP ${res.status}`;
    throw new Error(`API error (${res.status}): ${msg}`);
  }

  const json = await res.json();
  const blocks = Array.isArray(json.content) ? json.content : [];
  const toolUse = blocks.find(b => b.type === 'tool_use' && b.name === 'record_invoice');

  if (!toolUse) {
    const textFallback = blocks.filter(b => b.type === 'text').map(b => b.text).join('\n').trim();
    if (textFallback) {
      throw new Error(`Model returned text instead of tool call. Response: ${textFallback.slice(0, 300)}`);
    }
    throw new Error('Model returned no tool call and no text. Response had no usable content.');
  }

  return {
    data: normalizeExtraction(toolUse.input),
    raw: json,
    ms: Math.round(performance.now() - t0)
  };
}

/** Normalize tool output for v0.3 fields; safe for older saved batches missing new keys. */
export function normalizeExtraction(data) {
  if (!data || typeof data !== 'object') return data;
  const lineItems = Array.isArray(data.line_items) ? data.line_items.map(li => ({
    description: li?.description ?? null,
    quantity:    typeof li?.quantity === 'number' ? li.quantity : li?.quantity ?? null,
    unit_price:  typeof li?.unit_price === 'number' ? li.unit_price : li?.unit_price ?? null,
    line_total:  typeof li?.line_total === 'number' ? li.line_total : li?.line_total ?? null,
    item_code:   li?.item_code != null ? String(li.item_code) : null
  })) : [];
  let salesTaxLines = data.sales_tax_lines;
  if (salesTaxLines == null) {
    salesTaxLines = null;
  } else if (!Array.isArray(salesTaxLines)) {
    salesTaxLines = null;
  } else {
    salesTaxLines = salesTaxLines.map(t => ({
      label:  t?.label != null ? String(t.label) : '',
      amount: typeof t?.amount === 'number' ? t.amount : t?.amount != null ? Number(t.amount) : null
    })).filter(t => t.label || (t.amount != null && !Number.isNaN(t.amount)));
    if (salesTaxLines.length === 0) salesTaxLines = null;
  }
  return { ...data, line_items: lineItems, sales_tax_lines: salesTaxLines };
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result; // "data:application/pdf;base64,XXXX"
      const comma = String(result).indexOf(',');
      resolve(comma === -1 ? String(result) : String(result).slice(comma + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
